import { routes } from "@/global/routes";
import {
  faqs,
  howItWorks,
  ClubSuiteContent,
  LibertyLoftContent,
  clubSuiteSuiteNames,
  libertyLoftSuiteNames,
  inventoryCardSuiteModal,
  Philadelphia76ersInventoryCard,
} from "./helpers";
import { inventoryTypeUIDs, vendorUUIDs } from "../../constants";
import {
  DefaultCheckoutTerms,
  DefaultRegistrationTerms,
} from "../../default_terms_and_conditions";

const bannerImage =
  "https://ss-leagues.s3.amazonaws.com/NBA/Philadelphia%2076ers/sixers-banner.png";
const backgroundImage =
  "https://ss-leagues.s3.amazonaws.com/NBA/Philadelphia%2076ers/sixers-banner.png";
const logoImage =
  "https://ss-leagues.s3.amazonaws.com/NBA/Philadelphia%2076ers/sixers-logo.png";

const ClubSuiteImages = [
  "https://ss-leagues.s3.amazonaws.com/NBA/Philadelphia%2076ers/Club%20Suite/3.26.25%2076ers%20Wells%20Fargo%20Center-59%20Josh%20Ferdico.jpg",
  "https://ss-leagues.s3.amazonaws.com/NBA/Philadelphia%2076ers/Club%20Suite/3.26.25%2076ers%20Wells%20Fargo%20Center-60%20Josh%20Ferdico.jpg",
  "https://ss-leagues.s3.amazonaws.com/NBA/Philadelphia%2076ers/Club%20Suite/3.26.25%2076ers%20Wells%20Fargo%20Center-68%20Josh%20Ferdico.jpg",
];

const LibertyLoftImages = [
  "https://ss-leagues.s3.amazonaws.com/NBA/Philadelphia%2076ers/Liberty%20Loft/3.26.25%2076ers%20Wells%20Fargo%20Center-10%20Josh%20Ferdico%20%281%29.jpg",
  "https://ss-leagues.s3.amazonaws.com/NBA/Philadelphia%2076ers/Liberty%20Loft/3.26.25%2076ers%20Wells%20Fargo%20Center-11%20Josh%20Ferdico%20%281%29.jpg",
];

import type { TeamConfig } from "../types";

const config: TeamConfig = {
  teamSchema: "philadelphia76ers_suites",
  browserTitle: "76ers Suites",
  packages: false,
  defaultRoute: routes.purchaseSeats,
  phoneNumber: "",
  teamName: "Philadelphia 76ers",

  // seat map configs
  seatMapConfigs: {
    venueId: "nam-us-00048-sixers-sec",
    mapId: "blockmap2d",

    suiteImages: {
      ClubSuiteA: ClubSuiteImages,
      ClubSuiteB: ClubSuiteImages,
      ClubSuiteC: ClubSuiteImages,
      ClubSuiteD: ClubSuiteImages,
      ClubSuiteE: ClubSuiteImages,
      ClubSuiteF: ClubSuiteImages,
      ClubSuiteG: ClubSuiteImages,
      ClubSuiteH: ClubSuiteImages,
      ClubSuiteI: ClubSuiteImages,
      ClubSuiteJ: ClubSuiteImages,
      ClubSuiteK: ClubSuiteImages,
      ClubSuiteL: ClubSuiteImages,
      ClubSuiteM: ClubSuiteImages,
      ClubSuiteN: ClubSuiteImages,
      ClubSuiteO: ClubSuiteImages,
      ClubSuiteP: ClubSuiteImages,
      ClubSuiteQ: ClubSuiteImages,
      ClubSuiteR: ClubSuiteImages,
      LibertyLoft1: LibertyLoftImages,
      LibertyLoft2: LibertyLoftImages,
      LibertyLoft3: LibertyLoftImages,
      LibertyLoft4: LibertyLoftImages,
      LibertyLoft5: LibertyLoftImages,
    },
  },

  // gift cards
  giftCardConfigs: {
    isMenuGiftCardEnabled: false,
    isLandingPageGiftCardEnabled: false,
    isStandalonePageGiftCardEnabled: false,
    redirectGiftCardUrl: routes.redeem,
  },

  // purchase credit configs
  purchaseCreditConfigs: {
    isLandingPurchaseCreditEnabled: false,
    isStandAlonePurchaseCreditEnabled: false,
    showPurchaseCreditLinkInHomeMenu: false,
    isPurchaseCreditHistoryEnabled: false,
  },

  // disabled routes
  disabledRoutes: [],

  // switches
  checkoutCreditPackages: false,
  availableCreditEnabled: false,
  isCrowdHandlerEnabled: true,
  inventoryType: inventoryTypeUIDs.suiteLevelV2,

  // purchase seats config
  purchaseSeatConfigs: {
    minGameSelectionCount: 1,
    minSeatLimit: 1,
    isSimilarSeatForAllEvents: false,
    allowManualCartInSimilar: false,
    isCheckoutEditSeatsEnabled: false,
    isDiffSeatAlertEnabled: false,

    isCheckoutAdditionalDetailsEnabled: true,
    isPerksEnabled: true,
    invPreviewAccountGroup: ["0"],
    allowCheckoutFromPreview: true,
  },

  // feature configs
  packageConfigs: {
    isPackageEnabled: false,
    canRemovePackageGames: false,
    staticBuildYourOwn: null,
  },

  // intercom settings
  intercomConfigs: {
    isIntercomEnabled: true,
    allowedPages: [],
  },

  // checkout validations
  checkoutValidation: {
    allowedZipFailCount: Infinity,
    allowedPaymentFailCount: Infinity,
  },

  // analytics configs
  analyticsConfigs: {
    googleAnalyticsId: "",
    attrkPixelId: "",
    beaconId: "",
    googleTagManagerId: "",
  },

  // redirect configs
  redirectConfigs: {
    internalRedirectUrl: "",
    externalRedirectUrl: "",
  },

  refreshInventoryWhenEventClick: false,

  isSaveCardAllowed: false,

  // urls
  accountManagerUrl: "https://am.ticketmaster.com/sixers/",

  // vendor
  platform: vendorUUIDs.ticketmaster,

  // okta config
  oktaIssuer: "",
  oktaClientId: "",
  shouldUseInternalLogin: false,

  isBrainTreePaymentEnabled: false,

  // images
  images: {
    logoNameImage: "",
    faviconImage: logoImage,
    giftCardStandalonePageImage: "",
    findGamesBannerImage: bannerImage,
    authBackgroundImage: backgroundImage,
    checkoutStatusPageImage: backgroundImage,
    seatMap3dPreviewImage: backgroundImage,
    logoImage: logoImage + "?width=128&height=66",
  },

  uiConfigs: {
    bannerImage: {
      title: "",
      isBackgroundBasedBanner: true,
    },

    featuredGameCard: {
      showLogoWithTitle: false,
      hideOverlayLogo: true,
    },

    bottomBar: {
      showGameSelectionProgress: true,
    },

    hidePackageBalanceOnCheckout: true,
    hideServiceFee: true,

    inventoryCardSettings: {
      isPBLineItemsVisible: true,
      seatCountLabel: "Tickets Included",
      pbLineItemsValue: 5,

      suiteLevelInventoryCard: {
        customButtons: [
          {
            id: 1,
            label: "More Info",
            onClick: () => {
              inventoryCardSuiteModal.getState().set({
                isOpen: true,
                title: "More Info",
                content: <ClubSuiteContent />,
              });
            },
            suiteNames: clubSuiteSuiteNames,
          },
          {
            id: 2,
            label: "More Info",
            onClick: () => {
              inventoryCardSuiteModal.getState().set({
                isOpen: true,
                title: "More Info",
                content: <LibertyLoftContent />,
              });
            },
            suiteNames: libertyLoftSuiteNames,
          },
        ],
        renderBody: [
          {
            id: 1,
            component: () => <Philadelphia76ersInventoryCard />,
          },
        ],
      },
    },
    inventoryContainer: {
      getGridCellHeight: (arg) => (arg?.mdDown ? 260 : 180),
    },

    gameFiltersSettings: {
      hideSectionFilter: true,
      hideAccessibilityFilter: true,
    },

    checkoutPage: {
      checkoutForm: {
        optionalAdditionalDetailsCompanyName: true,
        optionalAdditionalDetailsCateringName: true,
        optionalAdditionalDetailsCateringEmail: true,
        optionalAdditionalDetailsCateringPhone: true,
        optionalAdditionalDetailsContractTitle: true,
        optionalAdditionalDetailsContractEmail: true,
        optionalAdditionalDetailsContractLastName: true,
        optionalAdditionalDetailsContractFirstName: true,
        optionalAdditionalDetailsDisclaimerFullName: true,
        optionalAdditionalDetailsContractPhoneNumber: true,

        sameAsContractSignerDefaultChecked: true,

        hideContractTitle: true,
        hideContractLastName: true,
        hideContractFirstName: true,
        hideCateringAndTicketContact: false,
      },
    },

    perks: {
      hideCateringPerkItem: true,
      hidePremiumPerkItem: true,
    },
  },

  colors: {
    primary: "#003da6",
  },

  howItWorks: howItWorks,
  faqs: faqs,

  languages: {
    topNavMenuItems: null,
    checkoutTermsAndCondition: <DefaultCheckoutTerms />,
    registrationTermsAndCondition: <DefaultRegistrationTerms />,
    purchaseSeats: {
      progressBarText: "Select 1 game to continue!",
      progressBarSuccessText:
        "You're good to go! Add more events or select Continue to view seating options.",
      chooseSeatsTabTitle: "Choose Suite",
      progressBarAditionalText: " to continue!",
      suiteLanguageDisclaimer:
        "Please note that individual suites may vary from the photos displayed.",
    },
    purchaseGiftCard: null,
    redeemGiftCard: null,

    checkoutSuccessMessage: "Your purchase is complete!",

    giftCard: {
      title: "",
      createGiftCard: {
        description: "",
        recipient: {
          sendNowFalse: {
            description: "",
          },
          sendNowTrue: {
            description: "",
          },
        },
      },
      redeemGiftCard: {
        loggedIn: {
          description: "",
        },
        logout: {
          description: "",
        },
        success: {
          description: "",
        },
      },
    },

    signInAuthText:
      "Connect your Ticketmaster account to continue with seamless access.",
  },
};

export default config;
